import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type { FindCertificatesKpiQueryResponse } from "../types/FindCertificatesKpi"

type FindCertificatesKpiClient = typeof client<
  FindCertificatesKpiQueryResponse,
  Error,
  never
>
type FindCertificatesKpi = {
  data: FindCertificatesKpiQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: never
  headerParams: never
  response: Awaited<ReturnType<FindCertificatesKpiClient>>
  client: {
    parameters: Partial<Parameters<FindCertificatesKpiClient>[0]>
    return: Awaited<ReturnType<FindCertificatesKpiClient>>
  }
}
export const findCertificatesKpiQueryKey = () =>
  [{ url: "/v1/certificate/find-certificates-kpi" }] as const
export type FindCertificatesKpiQueryKey = ReturnType<
  typeof findCertificatesKpiQueryKey
>
export function findCertificatesKpiQueryOptions<
  TData = FindCertificatesKpi["response"],
  TQueryData = FindCertificatesKpi["response"],
>(
  options: FindCertificatesKpi["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindCertificatesKpi["response"],
    FindCertificatesKpi["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findCertificatesKpiQueryKey()
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<
        FindCertificatesKpi["data"],
        FindCertificatesKpi["error"]
      >({
        method: "get",
        url: `/v1/certificate/find-certificates-kpi`,
        ...options,
      })
      return res
    },
  }
}
/**
 * @summary Find certificates kpi
 * @link /v1/certificate/find-certificates-kpi
 */
export function useFindCertificatesKpi<
  TData = FindCertificatesKpi["response"],
  TQueryData = FindCertificatesKpi["response"],
  TQueryKey extends QueryKey = FindCertificatesKpiQueryKey,
>(
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindCertificatesKpi["response"],
        FindCertificatesKpi["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindCertificatesKpi["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindCertificatesKpi["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findCertificatesKpiQueryKey()
  const query = useQuery<
    FindCertificatesKpi["data"],
    FindCertificatesKpi["error"],
    TData,
    any
  >({
    ...findCertificatesKpiQueryOptions<TData, TQueryData>(clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindCertificatesKpi["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
